import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Camera, 
  Users, 
  Check,
  Phone,
  Mail,
  MapPin,
  Play,
  Star,
  Award,
  Heart,
  X,
  Sparkles,
  ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

// Default content
const DEFAULT_CONTENT = {
  hero: {
    title: 'Portrait Chocolate',
    subtitle: 'Live Photo Booth',
    tagline: 'The Most Delicious Photo Booth Experience in Hawaii!',
    description: 'Take fun photos and get them printed on premium chocolate! Perfect for weddings, parties, corporate events & more.',
    button_text: 'Book Now',
    button_link: '/contact',
    background_video: '',
    background_image: '',
    hero_videos: []
  },
  features: [
    { title: 'Flexible', description: 'We offer a variety of solutions to accommodate your party needs.', icon: 'flexible' },
    { title: 'Affordable', description: 'Cutting edge and friendly service at competitive prices.', icon: 'affordable' },
    { title: 'Quality', description: 'Top quality services with attention to cleanliness and appearance.', icon: 'quality' }
  ],
  commitment: {
    title: 'Our Commitment to Excellence!',
    description: 'At Portrait Chocolate, we are dedicated to providing the highest level of customer service and satisfaction. Every event receives our full attention, from weddings to corporate functions, with modern photo booth equipment, professional setup, and a friendly, local team.'
  },
  stats: [
    { value: 100, label: 'Client Satisfaction', suffix: '%' },
    { value: 5, label: 'Star Rating', suffix: '' },
    { value: 100, label: 'Customizable Templates', suffix: '%' }
  ],
  includes: [
    { icon: 'camera', title: 'Professional Camera Setup', description: 'High-quality DSLR camera with perfect lighting for stunning photos' },
    { icon: 'props', title: 'Fun Props & Accessories', description: 'Hats, glasses, signs, boas, and lots of unique Hawaiian props!' },
    { icon: 'backdrop', title: 'Choice of Backdrops', description: 'Select from our beautiful backdrops or bring your own banner' },
    { icon: 'chocolate', title: 'Chocolate Photo Prints', description: 'Get your photos printed on premium edible chocolate!' },
    { icon: 'share', title: 'Digital Sharing', description: 'Instantly share photos via text or download to your phone' },
    { icon: 'attendant', title: 'Friendly Attendant', description: 'Professional staff to help guests and keep the fun going!' }
  ],
  gallery: [],
  backdrops: [],
  packages: [
    {
      name: 'Digital Booth',
      price: '375',
      duration: '2 Hours',
      features: ['Unlimited Digital Captures', 'Digital Booth', 'All props included'],
      note: '',
      popular: false
    },
    {
      name: 'Premium Booth',
      price: '575',
      duration: '2 Hours',
      features: ['Unlimited Prints', 'Digital Booth', '50 chocolate prints', 'Premium backdrops'],
      note: 'Additional prints: $3 each',
      popular: true
    },
    {
      name: '360 Premium Booth',
      price: '795',
      duration: '3 Hours',
      features: ['Unlimited Digital Captures', '360° Video Booth', 'Premium backdrops', 'Custom branding'],
      note: '',
      popular: false
    }
  ],
  events: [
    { emoji: '💒', label: 'Weddings' },
    { emoji: '🎂', label: 'Birthdays' },
    { emoji: '🎓', label: 'Graduations' },
    { emoji: '🏢', label: 'Corporate' },
    { emoji: '🎉', label: 'Parties' },
    { emoji: '🌺', label: 'Luaus' }
  ],
  contact: {
    email: 'potraitchocolate@gmail.com',
    phone: '(808) 756-2017',
    location: 'Big Island, Hawaii',
    travel_note: 'Travel fee: $20 per hour away from Kailua-Kona'
  },
  videos: [],
  discount: {
    enabled: true,
    title: 'Special Offer!',
    subtitle: 'Book Now & Save',
    code: 'ALOHA15',
    discount_text: '15% OFF',
    description: 'Use code at checkout for your first booking!',
    expiry: 'Limited time offer'
  }
};

// Icon mapping
const getIcon = (iconType) => {
  switch(iconType) {
    case 'camera': return <Camera className="w-8 h-8 text-amber-600" />;
    case 'props': return <span className="text-3xl">🎭</span>;
    case 'backdrop': return <span className="text-3xl">🖼️</span>;
    case 'chocolate': return <span className="text-3xl">🍫</span>;
    case 'share': return <span className="text-3xl">📱</span>;
    case 'attendant': return <Users className="w-8 h-8 text-amber-600" />;
    case 'flexible': return <Sparkles className="w-10 h-10 text-amber-500" />;
    case 'affordable': return <Star className="w-10 h-10 text-amber-500" />;
    case 'quality': return <Award className="w-10 h-10 text-amber-500" />;
    default: return <span className="text-3xl">⭐</span>;
  }
};

// Get YouTube embed URL
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&\n?#]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let start = 0;
    const end = parseInt(value);
    const increment = end / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    
    return () => clearInterval(timer);
  }, [value, duration]);
  
  return <span>{count}{suffix}</span>;
};

// Discount Popup Component
const DiscountPopup = ({ discount, onClose }) => {
  if (!discount?.enabled) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-3xl p-1 max-w-md w-full shadow-2xl animate-scale-in">
        <div className="bg-white rounded-3xl p-8 text-center relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500"></div>
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-amber-100 rounded-full opacity-50"></div>
          <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-orange-100 rounded-full opacity-50"></div>
          
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          
          {/* Content */}
          <div className="relative z-10">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{discount.title}</h3>
            <p className="text-gray-600 mb-4">{discount.subtitle}</p>
            
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-4xl font-bold py-4 px-6 rounded-2xl mb-4 shadow-lg">
              {discount.discount_text}
            </div>
            
            <div className="bg-gray-100 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-600 mb-2">Use code:</p>
              <div className="bg-white border-2 border-dashed border-amber-400 rounded-lg py-3 px-4">
                <span className="text-2xl font-mono font-bold text-amber-600 tracking-wider">{discount.code}</span>
              </div>
            </div>
            
            <p className="text-gray-600 text-sm mb-4">{discount.description}</p>
            
            <Link to="/contact">
              <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-6 text-lg font-semibold rounded-xl shadow-lg">
                Book Now & Save
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            
            {discount.expiry && (
              <p className="text-xs text-gray-500 mt-4">{discount.expiry}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const LivePhotoBooth = () => {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [showDiscount, setShowDiscount] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API}/photobooth-page`);
        if (response.ok) {
          const data = await response.json();
          if (data && Object.keys(data).length > 0) {
            setContent(prev => ({ ...prev, ...data }));
          }
        }
      } catch (error) {
        console.log('Using default content');
      } finally {
        setLoading(false);
      }
    };
    
    fetchContent();
    
    // Show discount popup after 3 seconds
    const timer = setTimeout(() => {
      setShowDiscount(true);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer for stats animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.5 }
    );
    
    const statsSection = document.getElementById('stats-section');
    if (statsSection) observer.observe(statsSection);
    
    return () => observer.disconnect();
  }, [loading]);

  const { hero, features, commitment, stats, includes, gallery, backdrops, packages, events, contact, videos, discount } = content;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Discount Popup */}
      {showDiscount && discount?.enabled && (
        <DiscountPopup discount={discount} onClose={() => setShowDiscount(false)} />
      )}

      {/* Hero Section - Paradise Parties Style */}
      <div className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background */}
        {hero.background_video ? (
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={hero.background_video} type="video/mp4" />
          </video>
        ) : hero.background_image ? (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${hero.background_image})` }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" />
        )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/50"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center text-white">
          <h2 className="text-lg md:text-xl font-medium text-amber-400 mb-4 tracking-wider uppercase">
            Welcome to {hero.title}
          </h2>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            Hawaii's Best<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400">
              {hero.subtitle}
            </span><br />
            Experience Starts Here
          </h1>
          <p className="text-lg md:text-xl mb-8 max-w-3xl mx-auto text-gray-300">
            {hero.description}
          </p>
          <Link to={hero.button_link || '/contact'}>
            <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-10 py-7 text-xl font-semibold rounded-full shadow-2xl shadow-amber-500/30 transition-all hover:scale-105">
              {hero.button_text}
              <ChevronRight className="w-6 h-6 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero Videos Section */}
      {hero.hero_videos && hero.hero_videos.length > 0 && (
        <div className="bg-gradient-to-b from-slate-900 to-slate-800 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-3xl font-bold text-center text-white mb-10">
              Watch Our Photo Booth in Action
            </h3>
            <div className={`grid gap-6 ${hero.hero_videos.length === 1 ? 'max-w-3xl mx-auto' : hero.hero_videos.length === 2 ? 'md:grid-cols-2 max-w-5xl mx-auto' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
              {hero.hero_videos.map((video, index) => {
                const youtubeUrl = getYouTubeEmbedUrl(video.url);
                const isBase64Video = video.url?.startsWith('data:video');
                return (
                  <div key={index} className="rounded-2xl overflow-hidden shadow-2xl bg-black group hover:scale-[1.02] transition-transform">
                    {youtubeUrl ? (
                      <div className="aspect-video">
                        <iframe 
                          src={youtubeUrl}
                          title={video.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <video 
                        controls 
                        className="w-full aspect-video"
                        poster={video.thumbnail}
                      >
                        <source src={video.url} type={isBase64Video ? "video/mp4" : "video/mp4"} />
                        Your browser does not support the video tag.
                      </video>
                    )}
                    {video.title && (
                      <div className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10">
                        <h4 className="font-semibold text-white text-center">{video.title}</h4>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Features Section - 3 Columns */}
      <div className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {(features || DEFAULT_CONTENT.features).map((feature, index) => (
              <div key={index} className="text-center p-8 rounded-2xl bg-white shadow-xl hover:shadow-2xl transition-shadow border border-gray-100">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  {getIcon(feature.icon)}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Commitment & Stats Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Commitment Text */}
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                {commitment?.title || DEFAULT_CONTENT.commitment.title}
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {commitment?.description || DEFAULT_CONTENT.commitment.description}
              </p>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-amber-500 text-amber-600 hover:bg-amber-50 px-8 py-4 text-lg font-semibold rounded-full">
                  About Us
                </Button>
              </Link>
            </div>
            
            {/* Stats */}
            <div id="stats-section" className="grid grid-cols-3 gap-4">
              {(stats || DEFAULT_CONTENT.stats).map((stat, index) => (
                <div key={index} className="text-center p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl">
                  <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500 mb-2">
                    {statsVisible ? (
                      <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                    ) : (
                      `0${stat.suffix}`
                    )}
                  </div>
                  <p className="text-gray-600 text-sm font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section - Paradise Parties Style */}
      <div className="py-20 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Our Packages</h2>
            <p className="text-gray-400 text-lg">Choose the perfect package for your event</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <Card key={index} className={`relative overflow-hidden bg-gradient-to-br ${pkg.popular ? 'from-amber-500 to-orange-500 text-white' : 'from-white to-gray-50'} rounded-3xl p-8 hover:scale-[1.02] transition-transform`}>
                {pkg.popular && (
                  <div className="absolute top-4 right-4 bg-white text-amber-600 text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <div className={`text-sm font-medium mb-2 ${pkg.popular ? 'text-amber-100' : 'text-gray-500'}`}>
                  Starting at :
                </div>
                <div className={`text-5xl font-bold mb-2 ${pkg.popular ? 'text-white' : 'text-gray-900'}`}>
                  ${pkg.price}
                </div>
                <h3 className={`text-2xl font-bold mb-4 ${pkg.popular ? 'text-white' : 'text-gray-900'}`}>
                  {pkg.name}
                </h3>
                <p className={`text-sm mb-6 ${pkg.popular ? 'text-amber-100' : 'text-gray-500'}`}>
                  {pkg.duration}
                </p>
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, fi) => (
                    <li key={fi} className={`flex items-center gap-3 ${pkg.popular ? 'text-white' : 'text-gray-700'}`}>
                      <Check className={`w-5 h-5 flex-shrink-0 ${pkg.popular ? 'text-amber-200' : 'text-amber-500'}`} /> 
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {pkg.note && (
                  <p className={`text-sm mb-6 ${pkg.popular ? 'text-amber-100' : 'text-gray-500'}`}>
                    {pkg.note}
                  </p>
                )}
                <Link to="/contact">
                  <Button className={`w-full py-4 font-semibold rounded-xl ${pkg.popular ? 'bg-white text-amber-600 hover:bg-gray-100' : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'}`}>
                    Book Now
                  </Button>
                </Link>
              </Card>
            ))}
          </div>
          
          {contact.travel_note && (
            <p className="text-center text-gray-400 mt-8">
              {contact.travel_note}
            </p>
          )}
        </div>
      </div>

      {/* Gallery Section */}
      {gallery && gallery.length > 0 && (
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">Photo Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map((photo, index) => (
                <div key={index} className="aspect-square rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:scale-[1.02] group">
                  <img 
                    src={photo.url} 
                    alt={photo.alt || 'Gallery photo'} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Backdrops Section */}
      {backdrops && backdrops.length > 0 && (
        <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Backdrop</h2>
              <p className="text-gray-600 text-lg">Select from our collection or bring your own</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {backdrops.map((backdrop, index) => (
                <div key={index} className="group cursor-pointer">
                  <div className="aspect-[3/2] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                    <img 
                      src={backdrop.image} 
                      alt={backdrop.name} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                    />
                  </div>
                  <p className="text-center font-medium text-gray-700 mt-3">{backdrop.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* What's Included Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What's Included</h2>
            <p className="text-gray-600 text-lg">Everything you need for an unforgettable experience</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {includes.map((item, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-xl transition-shadow bg-gradient-to-br from-amber-50 to-orange-50 border-0 rounded-2xl">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
                  {getIcon(item.icon)}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Perfect For Section */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">Perfect For Any Event</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {events.map((event, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-lg transition-all hover:scale-105 cursor-pointer bg-white rounded-2xl border-0 shadow-md">
                <span className="text-5xl block mb-3">{event.emoji}</span>
                <span className="font-semibold text-gray-700">{event.label}</span>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="py-20 bg-gradient-to-br from-amber-500 to-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h4 className="text-amber-200 font-medium mb-2">Why Choose Us</h4>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Hawaii's Luxury Photo Booth Experts
              </h2>
              <p className="text-lg text-amber-100 mb-8 leading-relaxed">
                Portrait Chocolate delivers the top-rated photo booth rental experience for weddings, parties, and corporate events across Hawaii. Our luxury photo booths bring fun, excitement, and lasting memories to any celebration.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/20 backdrop-blur rounded-2xl p-6 text-center">
                  <div className="text-3xl font-bold mb-1">100%</div>
                  <div className="text-amber-200 text-sm">Party Fun</div>
                </div>
                <div className="bg-white/20 backdrop-blur rounded-2xl p-6 text-center">
                  <div className="text-3xl font-bold mb-1">100%</div>
                  <div className="text-amber-200 text-sm">Satisfied Clients</div>
                </div>
              </div>
            </div>
            <div className="text-center">
              <Link to="/contact">
                <Button className="bg-white text-amber-600 hover:bg-gray-100 px-10 py-6 text-xl font-semibold rounded-full shadow-xl">
                  Get a Free Quote
                  <ChevronRight className="w-6 h-6 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 md:p-12 rounded-3xl">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">Ready to Book?</h3>
                <p className="text-gray-300 mb-6">
                  Contact us today to reserve your date! We would love to make your event extra special with our unique photo chocolate experience.
                </p>
                
                <div className="space-y-4">
                  <a href={`mailto:${contact.email}`} className="flex items-center gap-3 text-gray-300 hover:text-amber-400 transition-colors">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-amber-400" />
                    </div>
                    <span>{contact.email}</span>
                  </a>
                  
                  <a href={`tel:${contact.phone?.replace(/[^\d+]/g, '')}`} className="flex items-center gap-3 text-gray-300 hover:text-amber-400 transition-colors">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-amber-400" />
                    </div>
                    <span>{contact.phone}</span>
                  </a>
                  
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-amber-400" />
                    </div>
                    <span>{contact.location}</span>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-10 py-6 text-xl font-semibold rounded-full shadow-xl w-full md:w-auto">
                    Contact Us to Book
                    <ChevronRight className="w-6 h-6 ml-2" />
                  </Button>
                </Link>
                <p className="mt-4 text-sm text-gray-400">
                  Or call us directly for faster response!
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Back to Home */}
      <div className="text-center py-8 bg-gray-100">
        <Link to="/" className="text-amber-600 hover:text-amber-800 font-medium">
          ← Back to Portrait Chocolate Home
        </Link>
      </div>

      {/* Custom styles */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LivePhotoBooth;
